import { useCallback, useEffect, useRef, useState } from 'react';
const moveWithin = (order, movingId, targetId) => {
    const from = order.indexOf(movingId);
    const to = order.indexOf(targetId);
    const without = order.filter(id => id !== movingId);
    const targetIndex = without.indexOf(targetId);
    const insertAt = from < to ? targetIndex + 1 : targetIndex;
    return targetIndex < 0 ? order : [...without.slice(0, insertAt), movingId, ...without.slice(insertAt)];
};
const sameOrder = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);
// Dragging within this many px of the viewport's top or bottom scrolls the page, up to MAX_SCROLL px a frame.
const SCROLL_EDGE = 64;
const MAX_SCROLL = 16;
const scrollSpeed = (y) => {
    const intoTop = Math.min(SCROLL_EDGE - y, SCROLL_EDGE);
    const intoBottom = Math.min(y - (window.innerHeight - SCROLL_EDGE), SCROLL_EDGE);
    return intoTop > 0
        ? -Math.ceil(intoTop / SCROLL_EDGE * MAX_SCROLL)
        : intoBottom > 0
            ? Math.ceil(intoBottom / SCROLL_EDGE * MAX_SCROLL)
            : 0;
};
export const useSortable = ({ items, id, onReorder, disabled }) => {
    const [dragOrder, setDragOrder] = useState(null);
    const draggingIdRef = useRef(null);
    const dragOrderRef = useRef(null);
    const ids = items.map(id);
    const byId = new Map(items.map(item => [id(item), item]));
    const idsRef = useRef(ids);
    idsRef.current = ids;
    const byIdRef = useRef(byId);
    byIdRef.current = byId;
    const onReorderRef = useRef(onReorder);
    onReorderRef.current = onReorder;
    const orderIds = dragOrder !== null && dragOrder !== void 0 ? dragOrder : ids;
    const order = orderIds
        .map(itemId => byId.get(itemId))
        .filter((item) => item !== undefined);
    const draggingId = dragOrder ? draggingIdRef.current : null;
    const setOrder = useCallback((next) => {
        dragOrderRef.current = next;
        setDragOrder(next);
    }, []);
    const pointRef = useRef(null);
    const frameRef = useRef(null);
    const reorderAt = useCallback((x, y) => {
        var _a;
        const movingId = draggingIdRef.current;
        const current = dragOrderRef.current;
        const under = document.elementFromPoint(x, y);
        const targetId = (_a = under === null || under === void 0 ? void 0 : under.closest('[data-sortable-id]')) === null || _a === void 0 ? void 0 : _a.getAttribute('data-sortable-id');
        const next = movingId && current && targetId && targetId !== movingId
            ? moveWithin(current, movingId, targetId)
            : null;
        if (next && current && !sameOrder(next, current))
            setOrder(next);
    }, [setOrder]);
    const handleMove = useCallback((e) => {
        pointRef.current = { x: e.clientX, y: e.clientY };
        reorderAt(e.clientX, e.clientY);
    }, [reorderAt]);
    // Each frame of a drag, scrolls the page while the pointer is near an edge and re-checks what is under it.
    const autoScroll = useCallback(() => {
        const point = pointRef.current;
        const speed = point ? scrollSpeed(point.y) : 0;
        if (point && speed !== 0) {
            window.scrollBy(0, speed);
            reorderAt(point.x, point.y);
        }
        frameRef.current = requestAnimationFrame(autoScroll);
    }, [reorderAt]);
    const endDrag = useCallback(() => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', endDrag);
        window.removeEventListener('pointercancel', endDrag);
        if (frameRef.current !== null)
            cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
        pointRef.current = null;
        const final = dragOrderRef.current;
        const wasDragging = draggingIdRef.current !== null;
        draggingIdRef.current = null;
        dragOrderRef.current = null;
        setDragOrder(null);
        const finalItems = final === null || final === void 0 ? void 0 : final.map(itemId => byIdRef.current.get(itemId)).filter((item) => item !== undefined);
        if (wasDragging && final && finalItems && !sameOrder(final, idsRef.current))
            onReorderRef.current(finalItems);
    }, [handleMove]);
    const onPointerDown = useCallback((id) => (e) => {
        const canStart = !disabled && e.button === 0;
        if (canStart) {
            e.preventDefault();
            draggingIdRef.current = id;
            setOrder([...idsRef.current]);
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', endDrag);
            window.addEventListener('pointercancel', endDrag);
            frameRef.current = requestAnimationFrame(autoScroll);
        }
    }, [disabled, setOrder, handleMove, endDrag, autoScroll]);
    useEffect(() => endDrag, [endDrag]);
    const getItemProps = useCallback((id) => ({
        'data-sortable-id': id,
        'aria-grabbed': id === draggingId,
    }), [draggingId]);
    const getHandleProps = useCallback((id) => ({
        onPointerDown: onPointerDown(id),
        style: { cursor: draggingId ? 'grabbing' : 'grab', touchAction: 'none' },
    }), [onPointerDown, draggingId]);
    return { order, draggingId, getItemProps, getHandleProps };
};
